import { useAtom } from '@reatom/npm-react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, CheckMarkIcon } from '@blade/ui-kit'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { PlatformUtil } from '@root/shared'
import { LogoGlass, Separator as Separator_ } from '@root/shared/ui-kit'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

type OnboardingDialogAnnouncementProps = {
  onClose: () => void
}

export const OnboardingDialogAnnouncement = ({
  onClose,
}: OnboardingDialogAnnouncementProps) => {
  const { t } = useTranslation()
  const [dependencies] = useAtom(dependenciesAtom)
  const { policiesHost } = dependencies.environment

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    onClose()
  }
  const handleTermsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // when tauri
    PlatformUtil.openLink(e.currentTarget.href, '_blank')
  }

  return (
    <DialogAnnouncement
      css={css`
        --announcement-header-background: var(--appearance-1);
      `}
      withSeparator
      header={
        <Header>
          <LogoWrapper>
            <LogoGlass size={22} />
            <BrandName>{t('app.name')}</BrandName>
          </LogoWrapper>
          <IntroMessage>{t('onboarding-dialog.welcome')}</IntroMessage>
          <HeaderGradient />
        </Header>
      }
      footer={
        <Footer>
          <ButtonAlt
            variant="solid"
            color="primary"
            fullWidth
            radius="rounded"
            onClick={handleClick}
          >
            <CheckMarkIcon size={14} />
            <UnsafeUrlConfirmation>
              {t('onboarding-dialog.confirm')}
            </UnsafeUrlConfirmation>
          </ButtonAlt>
          <Links>
            <Link
              href={`${policiesHost}/en/privacy-policy`}
              target="_blank"
              onClick={handleTermsClick}
            >
              {t('onboarding-dialog.privacy-policy')}
            </Link>
            <Link
              href={`${policiesHost}/en/terms-of-service`}
              target="_blank"
              onClick={handleTermsClick}
            >
              {t('onboarding-dialog.terms-of-service')}
            </Link>
            <Link
              href={`${policiesHost}/en/disclaimer-terms`}
              target="_blank"
              onClick={handleTermsClick}
            >
              {t('onboarding-dialog.disclaimer')}
            </Link>
            <Link
              href={`${policiesHost}/en/boosting-terms`}
              target="_blank"
              onClick={handleTermsClick}
            >
              {t('onboarding-dialog.boosting-terms')}
            </Link>
          </Links>
        </Footer>
      }
    >
      <Body>
        <Heading>{t('onboarding-dialog.title')}</Heading>
        <Separator />
        <Description>
          <Trans
            i18nKey="onboarding-dialog.description"
            components={[<br key={0} />, <span key={1} />]}
          />
        </Description>
      </Body>
    </DialogAnnouncement>
  )
}

const Header = styled.div`
  height: 104px;
`

const LogoWrapper = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 8px;

  justify-items: center;
  align-content: center;
  justify-content: center;
  align-items: center;

  margin-top: 14px;

  z-index: 1;
  position: relative;
`

const BrandName = styled.span`
  font-size: 12px;
  font-weight: 600;
`

const Body = styled.div`
  color: var(--text-10);

  padding: 18px 22px 20px;
  font-size: 14px;

  text-align: center;
`

const Footer = styled.div`
  padding: 0 20px 20px;
  gap: 20px;

  display: grid;
  gap: 20px;
  place-items: center;
`

const Links = styled.div`
  text-align: center;
  display: flex;
  overflow: hidden;
`

const Link = styled.a`
  color: var(--textA-9);

  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  text-align: center;

  cursor: pointer;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--textA-11);
  }

  &:not(:last-child)::after {
    background: var(--backgroundA-3);

    content: '';
    margin-left: 6px;
    margin-right: 6px;

    width: 1px;
    height: 10px;
    display: inline-block;
    margin-bottom: -2px;
  }
`
const HeaderGradient = styled.div`
  background: linear-gradient(0deg, var(--primaryA-5) 40%, transparent 100%);
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 0;
  top: 60px;
  width: 100%;
`

const IntroMessage = styled.div`
  text-align: center;
  font-size: 62px;
  font-style: normal;
  font-weight: 700;

  background: linear-gradient(
    180deg,
    var(--backgroundA-9) 30%,
    transparent 100%
  );

  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  position: relative;
  z-index: 1;
  opacity: 0.4;

  text-transform: uppercase;

  html[lang='ru'] & {
    font-size: 32px;
    margin-top: 22px;
  }
`

const Heading = styled.h2`
  color: var(--text-12);
  text-align: center;

  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`

const Description = styled.p`
  color: var(--text-9);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  span {
    color: var(--text-11);
  }
`

const Separator = styled(Separator_)`
  margin: 14px 0;
  opacity: 0.4;
`

const ButtonAlt = styled(Button)`
  color: var(--textA-11);
  gap: 0;

  > svg {
    margin-inline-end: 6px;
    margin-inline-start: 4px;
  }
`

const UnsafeUrlConfirmation = styled.span`
  color: var(--text-12);

  font-weight: 700;
  height: 18px;

  display: grid;
  place-content: center;
`
